import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { X } from "lucide-react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, preventAccidentalDismiss } from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import RHFTextArea from "@/components/form/RHFTextArea";
import { FEEDBACK_LIMITS, FEEDBACK_TYPE_OPTIONS, FEEDBACK_TYPE_VALUES } from "@/config/dropdownData";
import { GENERIC_ERROR } from "@/lib/errorMessages";
import { FeedbackService } from "@/services/feedbackService";

import FeedbackImagePicker from "./FeedbackImagePicker";

// The server requires a type and a comment with something in it of at most
// FEEDBACK_LIMITS.commentMaxLength characters. The trimmed comment is what is
// sent, so the length the server measures is this one.
const feedbackSchema = z.object({
  type: z.string().refine((v) => FEEDBACK_TYPE_VALUES.includes(v), "Pick what your feedback is about"),
  comment: z
    .string()
    .trim()
    .min(1, "Tell us a little about it")
    .refine((v) => !v || FEEDBACK_LIMITS.hasLetter.test(v), FEEDBACK_LIMITS.messages.commentNeedsLetter)
    .refine((v) => v.length <= FEEDBACK_LIMITS.commentMaxLength, `Keep it to ${FEEDBACK_LIMITS.commentMaxLength} characters`),
  images: z.array(z.instanceof(File)).max(FEEDBACK_LIMITS.maxImages, FEEDBACK_LIMITS.messages.tooManyImages),
});

const DEFAULTS = { type: "", comment: "", images: [] };

const FieldError = ({ error }) =>
  error ? (
    <p role="alert" className="mt-1 text-xs text-status-error">
      {error.message}
    </p>
  ) : null;

/**
 * The form a tenant user sends product feedback from. Goes to the platform
 * team, and their organisation's tenant admin reads it on "Org Feedback", by
 * name — the description says so, because that changes what people are
 * willing to write.
 */
const FeedbackModal = ({ onClose, onSent }) => {
  // onChange: a comment the server would refuse says so as it is typed, not after Send.
  const methods = useForm({ resolver: zodResolver(feedbackSchema), mode: "onChange", defaultValues: DEFAULTS });
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (values) => {
    try {
      await FeedbackService.submit(values);
      toast.success("Thanks — your feedback was sent.");
      onSent?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.userMessage || GENERIC_ERROR);
    }
  };

  // While the request is in flight a close would drop the user's words with
  // no way to know whether they arrived.
  const close = () => !isSubmitting && onClose?.();

  return (
    <Dialog open onOpenChange={preventAccidentalDismiss(close)}>
      <DialogContent className="w-[340px] md:w-[560px] flex flex-col">
        <DialogHeader className="relative pl-5 pr-14 py-3 border-b border-border-primary shrink-0">
          <DialogTitle className="text-[15px] font-semibold text-text-primary">Send feedback</DialogTitle>
          <DialogDescription className="text-[12px] text-text-secondary mt-0.5">Goes to our product team and your organisation's admins.</DialogDescription>
          <DialogClose
            aria-label="Close"
            disabled={isSubmitting}
            className="absolute right-4 top-3 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center"
          >
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="px-5 py-4 space-y-5 max-h-[65vh] overflow-y-auto">
              <div>
                <p className="mb-2 text-sm text-text-secondary">
                  What is it about?<span className="text-status-error font-bold text-[16px] ml-1">*</span>
                </p>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <SegmentedControl
                      variant="chips"
                      ariaLabel="What is it about?"
                      options={FEEDBACK_TYPE_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  )}
                />
                <FieldError error={errors.type} />
              </div>

              <RHFTextArea
                id="feedback-comment"
                name="comment"
                label="Comments"
                placeholder="What happened, or what would make it better?"
                rows={5}
                maxLength={FEEDBACK_LIMITS.commentMaxLength}
                counter="near-limit"
                required
                disabled={isSubmitting}
              />

              <div>
                <p className="mb-2 text-sm text-text-secondary">Screenshots (optional)</p>
                <Controller
                  name="images"
                  control={control}
                  render={({ field }) => <FeedbackImagePicker value={field.value} onChange={field.onChange} disabled={isSubmitting} />}
                />
              </div>
            </div>

            <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
              <Button variant="tertiary" type="button" onClick={close} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="secondary" disabled={isSubmitting}>
                {isSubmitting ? "Sending…" : "Send feedback"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
